import React, { useState, useEffect } from 'react';
import {
  Gift, Settings, Trophy, Book, Ticket, Sparkles, Users,
  RefreshCw, Save, Trash2, Edit2, Check, X, Eye, EyeOff,
  CheckSquare, Square, Plus, Play
} from 'lucide-react';
import { Button } from '../components/ui/button';
import TombolaModal from './TombolaModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TombolaAdminTab = ({ token }) => {
  const [settings, setSettings] = useState(null);
  const [prizes, setPrizes] = useState([]);
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);
  const [editingPrize, setEditingPrize] = useState(null);
  const [view, setView] = useState('overview');
  const [selectedEntries, setSelectedEntries] = useState([]);
  const [deletingEntries, setDeletingEntries] = useState(false);
  const [selectedPrizes, setSelectedPrizes] = useState([]);
  const [deletingPrizes, setDeletingPrizes] = useState(false);
  const [initializingPrizes, setInitializingPrizes] = useState(false);
  const [showAddPrize, setShowAddPrize] = useState(false);
  const [newPrize, setNewPrize] = useState({
    name: '',
    name_nl: '',
    name_en: '',
    description_nl: '',
    description_en: '',
    value: 0,
    total_stock: 1,
    win_chance: 5,
    is_grand_prize: false
  });
  const [savingPrize, setSavingPrize] = useState(false);
  
  // Test Tombola modal state
  const [showTestModal, setShowTestModal] = useState(false);
  const [testVisualType, setTestVisualType] = useState(null);

  const visualTypes = [
    { value: 'scratch_card', label: 'Kraskaart', icon: '🎫' },
    { value: 'treasure_chest', label: 'Schatkist', icon: '📦' },
    { value: 'slot_machine', label: 'Slotmachine', icon: '🎰' }
  ];

  const prizeIcons = {
    grand_workshop: Trophy,
    ai_awareness: Sparkles,
    book: Book,
    hr_gala: Ticket,
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${token}` };

      const [settingsRes, prizesRes, entriesRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/tombola/admin/settings`, { headers }),
        fetch(`${API_URL}/api/tombola/admin/prizes`, { headers }),
        fetch(`${API_URL}/api/tombola/admin/entries?limit=50`, { headers }),
        fetch(`${API_URL}/api/tombola/admin/stats`, { headers })
      ]);

      if (settingsRes.ok) setSettings(await settingsRes.json());
      if (prizesRes.ok) setPrizes(await prizesRes.json());
      if (entriesRes.ok) setEntries(await entriesRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (error) {
      console.error('Failed to fetch tombola data:', error);
    } finally {
      setLoading(false);
    }
  };

  const initializePrizes = async () => {
    setInitializingPrizes(true);
    try {
      const res = await fetch(`${API_URL}/api/tombola/admin/init-prizes`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      alert(data.message);
      fetchData();
    } catch (error) {
      alert('Fout bij initialiseren: ' + error.message);
    } finally {
      setInitializingPrizes(false);
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await fetch(`${API_URL}/api/tombola/admin/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });
      alert('Instellingen opgeslagen!');
    } catch (error) {
      alert('Fout bij opslaan: ' + error.message);
    } finally {
      setSavingSettings(false);
    }
  };

  const updatePrize = async (prizeId, updates) => {
    try {
      await fetch(`${API_URL}/api/tombola/admin/prizes/${prizeId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });
      fetchData();
      setEditingPrize(null);
    } catch (error) {
      alert('Fout bij bijwerken: ' + error.message);
    }
  };

  const createPrize = async () => {
    if (!newPrize.name_nl || !newPrize.name_en) {
      alert('Vul de naam in (NL en EN)');
      return;
    }
    
    setSavingPrize(true);
    try {
      const res = await fetch(`${API_URL}/api/tombola/admin/prizes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newPrize.name || newPrize.name_en.toLowerCase().replace(/\s+/g, '_'),
          name_nl: newPrize.name_nl,
          name_en: newPrize.name_en,
          description_nl: newPrize.description_nl,
          description_en: newPrize.description_en,
          value: parseFloat(newPrize.value) || 0,
          total_stock: parseInt(newPrize.total_stock) || 1,
          win_chance: parseFloat(newPrize.win_chance) || 5,
          is_grand_prize: newPrize.is_grand_prize
        })
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Fout bij aanmaken');
      }
      
      // Reset form and close
      setNewPrize({
        name: '',
        name_nl: '',
        name_en: '',
        description_nl: '',
        description_en: '',
        value: 0,
        total_stock: 1,
        win_chance: 5,
        is_grand_prize: false
      });
      setShowAddPrize(false);
      fetchData();
      alert('Prijs succesvol toegevoegd!');
    } catch (error) {
      alert('Fout bij aanmaken: ' + error.message);
    } finally {
      setSavingPrize(false);
    }
  };

  const togglePrizeActive = async (prize) => {
    await updatePrize(prize.id, { is_active: !prize.is_active });
  };

  const toggleEntrySelection = (entryId) => {
    setSelectedEntries(prev =>
      prev.includes(entryId)
        ? prev.filter(id => id !== entryId)
        : [...prev, entryId]
    );
  };

  const toggleAllEntries = () => {
    if (selectedEntries.length === entries.length) {
      setSelectedEntries([]);
    } else {
      setSelectedEntries(entries.map(e => e.id));
    }
  };

  const deleteSelectedEntries = async () => {
    if (selectedEntries.length === 0) return;

    if (!window.confirm(`Weet je zeker dat je ${selectedEntries.length} deelnemer(s) wilt verwijderen?`)) {
      return;
    }

    setDeletingEntries(true);
    try {
      const response = await fetch(`${API_URL}/api/tombola/admin/entries/bulk/delete`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(selectedEntries)
      });

      if (response.ok) {
        setSelectedEntries([]);
        fetchData();
      } else {
        const error = await response.json();
        alert('Fout bij verwijderen: ' + (error.detail || 'Onbekende fout'));
      }
    } catch (error) {
      alert('Fout bij verwijderen: ' + error.message);
    } finally {
      setDeletingEntries(false);
    }
  };

  const deleteSingleEntry = async (entryId) => {
    if (!window.confirm('Weet je zeker dat je deze deelnemer wilt verwijderen?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/tombola/admin/entries/${entryId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchData();
      } else {
        const error = await response.json();
        alert('Fout bij verwijderen: ' + (error.detail || 'Onbekende fout'));
      }
    } catch (error) {
      alert('Fout bij verwijderen: ' + error.message);
    }
  };

  const togglePrizeSelection = (prizeId) => {
    setSelectedPrizes(prev =>
      prev.includes(prizeId)
        ? prev.filter(id => id !== prizeId)
        : [...prev, prizeId]
    );
  };

  const toggleAllPrizes = () => {
    if (selectedPrizes.length === prizes.length) {
      setSelectedPrizes([]);
    } else {
      setSelectedPrizes(prizes.map(p => p.id));
    }
  };

  const deleteSelectedPrizes = async () => {
    if (selectedPrizes.length === 0) return;

    if (!window.confirm(`Weet je zeker dat je ${selectedPrizes.length} prijs/prijzen wilt verwijderen?`)) {
      return;
    }

    setDeletingPrizes(true);
    try {
      for (const prizeId of selectedPrizes) {
        await fetch(`${API_URL}/api/tombola/admin/prizes/${prizeId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
      setSelectedPrizes([]);
      fetchData();
    } catch (error) {
      alert('Fout bij verwijderen: ' + error.message);
    } finally {
      setDeletingPrizes(false);
    }
  };

  const deleteSinglePrize = async (prizeId) => {
    if (!window.confirm('Weet je zeker dat je deze prijs wilt verwijderen?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/tombola/admin/prizes/${prizeId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        fetchData();
      } else {
        const error = await response.json();
        alert('Fout bij verwijderen: ' + (error.detail || 'Onbekende fout'));
      }
    } catch (error) {
      alert('Fout bij verwijderen: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tombola-admin-tab">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-amber-500 rounded-xl flex items-center justify-center">
            <Gift className="w-6 h-6 text-slate-900 dark:text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Tombola Beheer</h2>
            <p className="text-sm text-gray-500">Beheer prijzen, instellingen en bekijk deelnemers</p>
          </div>
        </div>
        <div className="flex gap-2">
          {prizes.length === 0 && (
            <Button
              onClick={initializePrizes}
              disabled={initializingPrizes}
              className="bg-green-600 hover:bg-green-700"
            >
              {initializingPrizes ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Gift className="w-4 h-4 mr-2" />}
              Initialiseer Prijzen
            </Button>
          )}
          <Button
            onClick={fetchData}
            variant="outline"
            size="sm"
            className="bg-white border-2 border-blue-500 text-blue-600 hover:bg-blue-50 font-semibold px-4"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Vernieuwen
          </Button>
        </div>
      </div>

      {/* Test Tombola Section */}
      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl p-4 border border-purple-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
              <Play className="w-5 h-5 text-slate-900 dark:text-white" />
            </div>
            <div>
              <h3 className="font-bold text-purple-900">Test Tombola</h3>
              <p className="text-sm text-purple-600">Direct testen zonder de quickscan te doorlopen</p>
            </div>
          </div>
          <div className="flex gap-2">
            {visualTypes.map((type) => (
              <Button
                key={type.value}
                onClick={() => {
                  setTestVisualType(type.value);
                  setShowTestModal(true);
                }}
                size="sm"
                variant="outline"
                className={`border-purple-300 hover:bg-purple-100 ${
                  settings?.visual_type === type.value ? 'bg-purple-100 border-purple-500' : ''
                }`}
              >
                <span className="mr-2">{type.icon}</span>
                {type.label}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Test Tombola Modal */}
      <TombolaModal
        isOpen={showTestModal}
        onClose={() => {
          setShowTestModal(false);
          setTestVisualType(null);
        }}
        userInfo={{
          name: 'Admin Test User',
          email: `admin_test_${Date.now()}@test.com`,
          company: 'Test Company',
          companySize: '51-200',
          jobTitle: 'HR Manager'
        }}
        assessmentId="admin-test-assessment"
        forceVisualType={testVisualType}
      />

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <p className="text-sm text-gray-500">Totaal Deelnemers</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total_entries}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <p className="text-sm text-gray-500">Totaal Spins</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total_spins}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <p className="text-sm text-gray-500">Prijzen Gewonnen</p>
            <p className="text-2xl font-bold text-green-600">{stats.total_prizes_won}</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-200">
            <p className="text-sm text-gray-500">Referrals</p>
            <p className="text-2xl font-bold text-blue-600">{stats.total_referrals}</p>
          </div>
        </div>
      )}

      <div className="flex gap-2 border-b border-gray-200">
        {['overview', 'prizes', 'entries'].map((tab) => (
          <button
            key={tab}
            onClick={() => setView(tab)}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
              view === tab
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
            data-testid={`tombola-tab-${tab}`}
          >
            {tab === 'overview' && 'Instellingen'}
            {tab === 'prizes' && 'Prijzen'}
            {tab === 'entries' && 'Deelnemers'}
          </button>
        ))}
      </div>

      {view === 'overview' && settings && (
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Tombola Instellingen
          </h3>

          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">Tombola Actief</p>
                <p className="text-sm text-gray-500">Schakel de tombola in of uit</p>
              </div>
              <button
                onClick={() => setSettings({ ...settings, is_active: !settings.is_active })}
                className={`relative w-14 h-7 rounded-full transition-colors ${
                  settings.is_active ? 'bg-green-500' : 'bg-gray-300'
                }`}
              >
                <div className={`absolute top-1 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                  settings.is_active ? 'left-8' : 'left-1'
                }`} />
              </button>
            </div>

            <div>
              <p className="font-medium text-gray-900 mb-2">Visueel Type</p>
              <div className="grid grid-cols-3 gap-3">
                {visualTypes.map((type) => (
                  <button
                    key={type.value}
                    onClick={() => setSettings({ ...settings, visual_type: type.value })}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      settings.visual_type === type.value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span className="text-3xl block mb-2">{type.icon}</span>
                    <span className="text-sm font-medium">{type.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <p className="font-medium text-gray-900 mb-2">Trekkingsdatum</p>
              <input
                type="date"
                value={settings.draw_date?.split('T')[0] || '2026-03-15'}
                onChange={(e) => setSettings({ ...settings, draw_date: e.target.value })}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <p className="font-medium text-gray-900 mb-2">Max Pogingen per Gebruiker</p>
              <input
                type="number"
                min="1"
                max="10"
                value={settings.max_spins_per_user || 3}
                onChange={(e) => setSettings({ ...settings, max_spins_per_user: parseInt(e.target.value) })}
                className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <p className="font-medium text-gray-900 mb-2">Referrals Vereist per Extra Poging</p>
              <input
                type="number"
                min="1"
                max="10"
                value={settings.referrals_required_per_spin || 3}
                onChange={(e) => setSettings({ ...settings, referrals_required_per_spin: parseInt(e.target.value) })}
                className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <Button onClick={saveSettings} disabled={savingSettings} className="bg-blue-500 hover:bg-blue-600">
              {savingSettings ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Instellingen Opslaan
            </Button>
          </div>
        </div>
      )}

      {view === 'prizes' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="font-bold text-gray-900">Prijzen Beheer</h3>
            <div className="flex gap-2">
              <Button
                onClick={() => setShowAddPrize(true)}
                size="sm"
                className="bg-green-600 hover:bg-green-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Prijs Toevoegen
              </Button>
              {selectedPrizes.length > 0 && (
                <Button
                  onClick={deleteSelectedPrizes}
                  disabled={deletingPrizes}
                  variant="destructive"
                  size="sm"
                  className="bg-red-600 hover:bg-red-700"
                >
                  {deletingPrizes ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-2" />
                  )}
                  Verwijder ({selectedPrizes.length})
                </Button>
              )}
            </div>
          </div>
          
          {/* Add Prize Form */}
          {showAddPrize && (
            <div className="p-4 bg-green-50 border-b border-green-200">
              <h4 className="font-bold text-green-800 mb-4 flex items-center gap-2">
                <Plus className="w-5 h-5" />
                Nieuwe Prijs Toevoegen
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Naam (NL) *</label>
                  <input
                    type="text"
                    value={newPrize.name_nl}
                    onChange={(e) => setNewPrize({...newPrize, name_nl: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="Bijv. Gratis Workshop"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Naam (EN) *</label>
                  <input
                    type="text"
                    value={newPrize.name_en}
                    onChange={(e) => setNewPrize({...newPrize, name_en: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="E.g. Free Workshop"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Waarde (€)</label>
                  <input
                    type="number"
                    value={newPrize.value}
                    onChange={(e) => setNewPrize({...newPrize, value: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="0"
                    min="0"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Beschrijving (NL)</label>
                  <input
                    type="text"
                    value={newPrize.description_nl}
                    onChange={(e) => setNewPrize({...newPrize, description_nl: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="Korte beschrijving..."
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Beschrijving (EN)</label>
                  <input
                    type="text"
                    value={newPrize.description_en}
                    onChange={(e) => setNewPrize({...newPrize, description_en: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="Short description..."
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Voorraad</label>
                  <input
                    type="number"
                    value={newPrize.total_stock}
                    onChange={(e) => setNewPrize({...newPrize, total_stock: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="1"
                    min="1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Winkans (%)</label>
                  <input
                    type="number"
                    value={newPrize.win_chance}
                    onChange={(e) => setNewPrize({...newPrize, win_chance: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="5"
                    min="0"
                    max="100"
                    step="0.1"
                  />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <input
                    type="checkbox"
                    id="is_grand_prize"
                    checked={newPrize.is_grand_prize}
                    onChange={(e) => setNewPrize({...newPrize, is_grand_prize: e.target.checked})}
                    className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                  />
                  <label htmlFor="is_grand_prize" className="text-sm font-medium text-gray-700">
                    Hoofdprijs (&gt;50 medewerkers + HR functie)
                  </label>
                </div>
              </div>
              <div className="mt-4 flex gap-2">
                <Button
                  onClick={createPrize}
                  disabled={savingPrize}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {savingPrize ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4 mr-2" />
                  )}
                  Prijs Toevoegen
                </Button>
                <Button
                  onClick={() => setShowAddPrize(false)}
                  variant="outline"
                  className="border-gray-300"
                >
                  <X className="w-4 h-4 mr-2" />
                  Annuleren
                </Button>
              </div>
            </div>
          )}
          
          {prizes.length === 0 && !showAddPrize ? (
            <div className="p-8 text-center text-gray-500">
              <Gift className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>Nog geen prijzen geconfigureerd.</p>
              <p className="text-sm mt-2">Klik op "Prijs Toevoegen" of "Initialiseer Prijzen" om te beginnen.</p>
            </div>
          ) : prizes.length > 0 && (
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <button onClick={toggleAllPrizes} className="flex items-center justify-center text-gray-500 hover:text-gray-700">
                      {selectedPrizes.length === prizes.length && prizes.length > 0 ? (
                        <CheckSquare className="w-5 h-5 text-blue-600" />
                      ) : (
                        <Square className="w-5 h-5" />
                      )}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Prijs</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Waarde</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Voorraad</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Winkans</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Acties</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {prizes.map((prize) => {
                  const PrizeIcon = prizeIcons[prize.name] || Gift;
                  const isEditing = editingPrize === prize.id;

                  return (
                    <tr key={prize.id} className={`hover:bg-gray-50 ${selectedPrizes.includes(prize.id) ? 'bg-blue-50' : ''}`}>
                      <td className="px-4 py-3">
                        <button onClick={() => togglePrizeSelection(prize.id)} className="flex items-center justify-center">
                          {selectedPrizes.includes(prize.id) ? (
                            <CheckSquare className="w-5 h-5 text-blue-600" />
                          ) : (
                            <Square className="w-5 h-5 text-gray-400 hover:text-gray-600" />
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${prize.is_grand_prize ? 'bg-amber-100' : 'bg-gray-100'}`}>
                            <PrizeIcon className={`w-5 h-5 ${prize.is_grand_prize ? 'text-amber-600' : 'text-gray-600'}`} />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{prize.name_nl}</p>
                            {prize.is_grand_prize && (
                              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Hoofdprijs</span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-900">{prize.value} euro</td>
                      <td className="px-4 py-3">
                        {isEditing ? (
                          <input type="number" min="0" defaultValue={prize.remaining_stock} className="w-16 px-2 py-1 border rounded" id={`stock-${prize.id}`} />
                        ) : (
                          <span className={`font-medium ${prize.remaining_stock === 0 ? 'text-red-600' : 'text-gray-900'}`}>
                            {prize.remaining_stock} / {prize.total_stock}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {isEditing ? (
                          <input type="number" min="0" max="100" step="0.1" defaultValue={prize.win_chance} className="w-20 px-2 py-1 border rounded" id={`chance-${prize.id}`} />
                        ) : (
                          <span className="text-gray-900">{prize.win_chance}%</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => togglePrizeActive(prize)}
                          className={`flex items-center gap-1 px-2 py-1 rounded text-sm font-medium ${prize.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}
                        >
                          {prize.is_active ? (<><Eye className="w-3 h-3" />Actief</>) : (<><EyeOff className="w-3 h-3" />Inactief</>)}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          {isEditing ? (
                            <>
                              <button
                                onClick={() => {
                                  const stock = document.getElementById(`stock-${prize.id}`).value;
                                  const chance = document.getElementById(`chance-${prize.id}`).value;
                                  updatePrize(prize.id, { remaining_stock: parseInt(stock), win_chance: parseFloat(chance) });
                                }}
                                className="p-1.5 bg-green-500 text-slate-900 dark:text-white rounded hover:bg-green-600"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                              <button onClick={() => setEditingPrize(null)} className="p-1.5 bg-gray-200 text-gray-600 rounded hover:bg-gray-300">
                                <X className="w-4 h-4" />
                              </button>
                            </>
                          ) : (
                            <>
                              <button onClick={() => setEditingPrize(prize.id)} className="p-1.5 text-gray-400 hover:text-gray-600" title="Bewerken">
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button onClick={() => deleteSinglePrize(prize.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title="Verwijderen">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {view === 'entries' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="p-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="font-bold text-gray-900">Deelnemers ({entries.length})</h3>
            {selectedEntries.length > 0 && (
              <Button
                onClick={deleteSelectedEntries}
                disabled={deletingEntries}
                variant="destructive"
                size="sm"
                className="bg-red-600 hover:bg-red-700"
              >
                {deletingEntries ? (<RefreshCw className="w-4 h-4 mr-2 animate-spin" />) : (<Trash2 className="w-4 h-4 mr-2" />)}
                Verwijder ({selectedEntries.length})
              </Button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <button onClick={toggleAllEntries} className="flex items-center justify-center text-gray-500 hover:text-gray-700">
                      {selectedEntries.length === entries.length && entries.length > 0 ? (<CheckSquare className="w-5 h-5 text-blue-600" />) : (<Square className="w-5 h-5" />)}
                    </button>
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Naam</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Bedrijf</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Pogingen</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Gewonnen</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Referrals</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Hoofdprijs?</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-500">Acties</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {entries.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">Nog geen deelnemers</td></tr>
                ) : (
                  entries.map((entry) => (
                    <tr key={entry.id} className={`hover:bg-gray-50 ${selectedEntries.includes(entry.id) ? 'bg-blue-50' : ''}`}>
                      <td className="px-4 py-3">
                        <button onClick={() => toggleEntrySelection(entry.id)} className="flex items-center justify-center">
                          {selectedEntries.includes(entry.id) ? (<CheckSquare className="w-5 h-5 text-blue-600" />) : (<Square className="w-5 h-5 text-gray-400 hover:text-gray-600" />)}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-gray-900">{entry.user_name}</p>
                          <p className="text-sm text-gray-500">{entry.user_email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-900">{entry.user_company}</td>
                      <td className="px-4 py-3"><span className="font-medium text-gray-900">{entry.spin_count}/3</span></td>
                      <td className="px-4 py-3">
                        {entry.prizes_won_details?.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                              {entry.prizes_won_details.map((prize, idx) => (
                                <span key={`${prize.name}-${idx}`} className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded">{prize.name}</span>
                            ))}
                          </div>
                        ) : (<span className="text-gray-400">-</span>)}
                      </td>
                      <td className="px-4 py-3"><span className="font-medium text-blue-600">{entry.referrals?.length || 0}</span></td>
                      <td className="px-4 py-3">
                        {entry.eligible_for_grand_prize ? (
                          <span className="bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded flex items-center gap-1 w-fit"><Trophy className="w-3 h-3" />Ja</span>
                        ) : (<span className="text-gray-400">Nee</span>)}
                      </td>
                      <td className="px-4 py-3">
                        <button onClick={() => deleteSingleEntry(entry.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title="Verwijderen">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default TombolaAdminTab;
